import React, { useContext, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SvgAttachToTable from '@components/ringil3/icons/menu/reservation/AttachToTable';
import SvgDuplicate from '@components/ringil3/icons/menu/reservation/Duplicate';
import SvgRepeatedReservations from '@components/ringil3/icons/menu/reservation/RepeatedReservations';
import SvgDelete from '@components/ringil3/icons/menu/reservation/Delete';
import { usePutOnTheTable } from '@components/ringil3/Features/Reservation/PutOnTheTable/PutOnTheTable';
import { ApiContext } from '@api/api';
import SvgCutReservation from '@components/ringil3/icons/reservation/CutReservation';
import { useCreateDeleteDialog } from '@components/ringil3/Dialog/DeleteDialog.tsx';
import SvgNotifyDriver from '@components/ringil3/icons/menu/reservation/NotifyDriver.tsx';
import { FeaturesEnum, PermissionTypesEnum } from '@api/ringil-api.v2.tsx';
import {
  useMessengerTimeslotGoToRampSidebar,
} from '@components/ringil3/Features/Expedition/Sidebars/MessengerTimeslotGoToRampSidebar.tsx';
import SvgMessage from '@icons/Message.tsx';
import {
  useMessengerTimeslotOnTimeCheckSidebar,
} from '@components/ringil3/Features/Expedition/Sidebars/MessengerTimeslotOnTimeCheckSidebar.tsx';
import { RuleInput, SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import MoreButton from '@components/RingilComponents/MoreButton/MoreButton.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';

type MenuItemSection = 'state' | 'related' | 'actions';

/** Menu sections in display order, with their headings — the same shape the expedition menu uses. */
const SECTIONS: { id: MenuItemSection; title: (tr: (key: string, fallback: string) => string) => string }[] = [
  { id: 'state', title: tr => tr('ButtonMoreNavigation.stateSection', 'State') },
  { id: 'related', title: tr => tr('ButtonMoreNavigation.relatedSection', 'Related') },
  { id: 'actions', title: tr => tr('ButtonMoreNavigation.actionsSection', 'Actions') },
];

interface ComponentProps {
  reloadHandler: () => void;
  isPinnedOnLocRampTable: boolean;
  locationId: string;
  reservationId: string;
  reservationAppId: string;
  isOwner: boolean;
  hasTable: boolean;
  hasTimeslot: boolean;
  hasPhone: boolean;
  onCopyReservation: (reservationId: string) => void;
  onChangeDuration: (reservationId: string) => void;
  onDuplicate: (reservationId: string) => void;
  onNotifyDriver: (reservationId: string) => void;
  driverPhoneNumber?: string;
  rampName?: string;
  onDelete?: () => void;
  location?: string;
  since?: string;
  className?: string;
  isArchived: boolean;
  canDelete: boolean;
  // Reservation's location is deleted with no replacement — nothing new can be created on it.
  locationDeleted?: boolean;
  // When true, the counter party is within the configured block window before start; archiving/deleting are blocked.
  editBlockedBeforeStart?: boolean;
  dataTestId?: string;
  secured?: SecuredConfig;
  /** Owner company has a webhook definition for reservations - resolved by the detail page only. */
  webhookAvailable?: boolean;
}

const ReservationMoreButtons: React.FC<ComponentProps> = ({
  reloadHandler,
  reservationId,
  reservationAppId,
  locationId,
  isPinnedOnLocRampTable,
  isOwner,
  onCopyReservation,
  onChangeDuration,
  onDuplicate,
  hasTable,
  hasTimeslot,
  hasPhone,
  onNotifyDriver,
  driverPhoneNumber,
  rampName,
  onDelete,
  location,
  since,
  className, isArchived, canDelete,
  locationDeleted = false,
  editBlockedBeforeStart = false,
  dataTestId,
  secured,
  webhookAvailable,
}) => {
  const { tr } = useTranslationLgs();
  const { deleteReservation, archiveReservation, manualWebhookReservationSend } = useContext(ApiContext);
  const [visibleSendWebhookDialog, setVisibleSendWebhookDialog] = useState(false);
  const [sendingWebhook, setSendingWebhook] = useState(false);
  const { decorate } = useSecuredDecoration(secured);

  const { DeleteDialog, setObjToDelete } = useCreateDeleteDialog();
  const { MessengerTimeslotGoToRampSidebar, setVisible: setVisibleMessengerGoToRampSidebar } = useMessengerTimeslotGoToRampSidebar();
  const { MessengerTimeslotOnTimeCheckSidebar, setVisible: setVisibleMessengerOnTimeCheckSidebar } = useMessengerTimeslotOnTimeCheckSidebar();

  const deleteReservationHandler = (reservationId: string) => {
    deleteReservation(reservationId, () => {
      reloadHandler();
      onDelete?.();
    });
  };

  const ownerState = { label: 'logged-in company owns reservation', satisfied: isOwner, description: 'Owner only: only the company that owns the reservation can do this', byDesign: true };
  const notArchivedState = { label: 'reservation is not archived', satisfied: !isArchived, description: 'Not offered on an archived reservation', byDesign: true };
  const hasTableState = { label: 'reservation has location ramp table', satisfied: hasTable, description: "Offered only when the reservation's location has a ramp table" };
  const hasTimeslotState = { label: 'reservation has a timeslot', satisfied: hasTimeslot, description: 'Offered only when the reservation has a booked time' };
  const hasPhoneState = { label: 'driver phone present', satisfied: hasPhone, description: "Offered only when the reservation has the driver's phone number", byDesign: false };

  const attachToTableFlag = useSecuredFlag('more-action-attach-to-table', {
    kind: 'action',
    description: 'Attach reservation to / detach from location ramp table',
    ownership: ownerState,
    state: [hasTableState, notArchivedState],
  });
  const notifyDriverFlag = useSecuredFlag('more-action-notify-driver', {
    kind: 'action',
    description: 'Notify driver via SMS',
    state: [hasPhoneState],
  });
  const duplicateFlag = useSecuredFlag('more-action-duplicate', {
    kind: 'action',
    description: 'Duplicate reservation',
  });
  // A superseded location is deleted too, but its copies are created on the replacement — only a
  // location deleted without one takes the action away (RD-2872).
  const locationTakesReservationsState = {
    label: 'reservation location is not deleted',
    satisfied: !locationDeleted,
    description: "Not offered when the reservation's location was deleted without a replacement",
    byDesign: true,
  };
  const repeatingFlag = useSecuredFlag('more-action-repeating', {
    kind: 'action',
    description: 'Create repeating reservations from this one',
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_DUPLICATE],
    state: [hasTimeslotState, locationTakesReservationsState],
  });
  const changeDurationFlag = useSecuredFlag('more-action-change-duration', {
    kind: 'action',
    description: 'Change reservation slot duration',
    ownership: ownerState,
    state: [hasTimeslotState, notArchivedState],
  });
  const editNotBlockedBeforeStartState = {
    label: 'edit not blocked before start',
    satisfied: !editBlockedBeforeStart,
    description: "A partner cannot change the reservation within the period before its start set in the reservation owner's settings",
    byDesign: false,
  };
  const deleteFlag = useSecuredFlag('more-action-delete', {
    kind: 'action',
    description: 'Delete reservation (per-side delete + permission)',
    state: [editNotBlockedBeforeStartState],
    custom: [
      {
        label: 'canDelete prop satisfied',
        satisfied: canDelete,
        description: "Deleting must be allowed for the user's side (owner or partner) in the reservation owner's settings, and the user needs the delete permission",
        byDesign: false,
      },
    ],
  });
  const sendWebhookFlag = useSecuredFlag('more-action-send-webhook', {
    kind: 'action',
    description: 'Queue the owner company webhooks for this reservation with current data (MANUAL_ACTION)',
    permissions: [PermissionTypesEnum.PERMISSION_TIMESLOT_DETAIL_RW],
    ownership: ownerState,
    custom: [{ label: 'company has a webhook definition', satisfied: webhookAvailable === true }],
  });
  const archiveFlag = useSecuredFlag('more-action-archive', {
    kind: 'action',
    description: 'Archive reservation',
    state: [{ label: 'not archived', satisfied: !isArchived, description: 'Not offered on a reservation that is already archived', byDesign: true }, editNotBlockedBeforeStartState],
  });
  const messengerGoToRampFlag = useSecuredFlag('more-action-messenger-go-to-ramp', {
    kind: 'action',
    description: 'Send WhatsApp "go to ramp" message',
    features: [FeaturesEnum.MESSENGER_TIMESLOT_GO_TO_RAMP],
  });
  const messengerOnTimeCheckFlag = useSecuredFlag('more-action-messenger-on-time-check', {
    kind: 'action',
    description: 'Send WhatsApp "on time check" message',
    features: [FeaturesEnum.MESSENGER_TIMESLOT_ON_TIME_CHECK],
  });

  const { PutOnTheTableComponent, putOnTheTable, removeFromTable } = usePutOnTheTable(
    () => {
      reloadHandler();
    },
    () => {
      reloadHandler();
    },
  );

  interface MenuItemConfig {
    id: string;
    label: string;
    icon: React.ReactNode;
    command: () => void;
    visible: RuleInput;
    section: MenuItemSection;
    dataTestId?: string;
  }

  const model: MenuItemConfig[] = [
    {
      id: 'attach-to-table',
      label: isPinnedOnLocRampTable
        ? tr(`ReservationMoreButtons.dettachToTable`, `Dettach from the table`)
        : tr(`ReservationMoreButtons.attachToTable`, `Attach to the table`),
      icon: <SvgAttachToTable />,
      command: () => {
        if (!isPinnedOnLocRampTable) {
          putOnTheTable(locationId, reservationId, '');
        } else {
          removeFromTable(locationId, reservationId);
        }
        reloadHandler();
      },
      visible: attachToTableFlag,
      section: 'actions',
    },
    {
      id: 'notify-driver',
      label: tr(`ReservationMoreButtons.notifyDriver`, `Notify driver`),
      icon: <SvgNotifyDriver />,
      command: () => onNotifyDriver(reservationId),
      visible: notifyDriverFlag,
      section: 'related',
      dataTestId: 'reservationDetail-moreButton-notifyDriver',
    },
    {
      id: 'duplicate',
      label: tr(`ReservationMoreButtons.duplicate`, `Duplicate`),
      icon: <SvgDuplicate />,
      command: () => onDuplicate?.(reservationId),
      visible: duplicateFlag,
      section: 'actions',
      dataTestId: 'reservationDetail-moreButton-duplicate',
    },
    {
      id: 'repeating-reservations',
      label: tr(`ReservationMoreButtons.repeatingReservations`, `Repeating reservations`),
      icon: <SvgRepeatedReservations />,
      command: () => onCopyReservation(reservationId),
      visible: repeatingFlag,
      section: 'actions',
      dataTestId: 'reservationDetail-moreButton-repeatingReservations',
    },
    {
      id: 'change-duration',
      label: tr(`ReservationMoreButtons.changeDuration`, `Change duration`),
      icon: <SvgCutReservation />,
      command: () => onChangeDuration(reservationId),
      visible: changeDurationFlag,
      section: 'state',
      dataTestId: 'reservationDetail-moreButton-changeDuration',
    },
    {
      id: 'delete',
      label: tr(`ReservationMoreButtons.deleteReservation`, `Delete reservation`),
      icon: <SvgDelete />,
      command: () => setObjToDelete(reservationId),
      visible: deleteFlag,
      section: 'state',
      dataTestId: 'reservationDetail-moreButton-delete',
    },
    {
      id: 'archive',
      label: tr(`ReservationMoreButtons.archiveReservation`, `Archive`),
      icon: <SvgDelete />,
      command: () => archiveReservation(reservationId, () => reloadHandler()),
      visible: archiveFlag,
      section: 'state',
      dataTestId: 'reservationDetail-moreButton-archive',
    },
    {
      id: 'send-webhook',
      label: tr(`ReservationMoreButtons.sendWebhook`, `Send webhook`),
      icon: <SvgMessage />,
      command: () => setVisibleSendWebhookDialog(true),
      visible: sendWebhookFlag,
      section: 'actions',
      dataTestId: 'reservationDetail-moreButton-sendWebhook',
    },
    {
      id: 'messenger-go-to-ramp',
      label: tr(`ReservationMoreButtons.messageTimeslotGoToRamp`, `Request driver arrival`),
      icon: <SvgMessage />,
      command: () => setVisibleMessengerGoToRampSidebar(true),
      visible: messengerGoToRampFlag,
      section: 'related',
      dataTestId: 'reservationDetail-moreButton-messageTimeslotGoToRamp',
    },
    {
      id: 'messenger-on-time-check',
      label: tr(`ReservationMoreButtons.messageTimeslotOnTimeCheck`, `Request on time check`),
      icon: <SvgMessage />,
      command: () => setVisibleMessengerOnTimeCheckSidebar(true),
      visible: messengerOnTimeCheckFlag,
      section: 'related',
      dataTestId: 'reservationDetail-moreButton-messageTimeslotOnTimeCheck',
    },
  ];

  return (
    <>{decorate(
    <div className={className}>
      <MoreButton
        buttonMode={'table'}
        buttonSize={'big'}
        dataTestId={dataTestId}
        sections={SECTIONS.map(section => ({
          title: section.title(tr),
          items: model
            .filter(item => item.section === section.id)
            .map(item => ({
              id: item.id,
              label: item.label,
              icon: item.icon,
              command: item.command,
              secured: item.visible,
              securedId: `more-reservation-${item.id}`,
              dataTestId: item.dataTestId,
            })),
        }))}
      />
      <ConfirmDialog
        visible={visibleSendWebhookDialog}
        dialogTitle={tr(`ReservationMoreButtons.sendWebhookTitle`, `Send webhook now?`)}
        dialogMessage={tr(
          `ReservationMoreButtons.sendWebhookBody`,
          `All webhooks configured for your company will be sent again with the current data of reservation {{applicationId}}.`,
          { applicationId: reservationAppId },
        )}
        onCancel={() => setVisibleSendWebhookDialog(false)}
        onConfirm={() => {
          setSendingWebhook(true);
          manualWebhookReservationSend(
            reservationId,
            () => {
              setSendingWebhook(false);
              setVisibleSendWebhookDialog(false);
              // Re-resolves webhookAvailable: the item stays hidden until the queued delivery is sent.
              reloadHandler();
            },
            null,
            {
              onError: (_error, showStandardError) => {
                showStandardError();
                setSendingWebhook(false);
              },
            },
          );
        }}
        onAcceptButtonText={tr(`ReservationMoreButtons.yesSendWebhook`, `Send`)}
        onCancelButtonText={tr(`generic.cancel`, `Cancel`)}
        loading={sendingWebhook}
        dataTestId='reservationDetail-confirmSendWebhook'
      />
      <PutOnTheTableComponent reservationAppId={reservationAppId} />
      <DeleteDialog
        onDelete={(reservationId: string) => {
          deleteReservationHandler(reservationId);
        }}
        dataTestId='reservationDetail-confirmDelete'
      />
      <MessengerTimeslotGoToRampSidebar reservationId={reservationId} applicationId={reservationAppId} phoneNumber={driverPhoneNumber || ''} ramp={rampName || ''} />
      <MessengerTimeslotOnTimeCheckSidebar
        reservationId={reservationId}
        applicationId={reservationAppId}
        phoneNumber={driverPhoneNumber || ''}
        location={location || ''}
        since={since || ''}
      />
    </div>,
    )}</>
  );
};







export default ReservationMoreButtons;
